import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Send, Paperclip, Eye, Trash2, RefreshCw, ScrollText, Mail, Save, Bold, Italic, Underline, Heading2, List, ListOrdered, Link2 } from "lucide-react";

const MAX_TOTAL_MB = 20;
const FREE_FROM_VALUE = "__free__";

function bytesToHuman(b) {
  if (!b) return "0";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function StatusBadge({ status }) {
  if (status === "opened")
    return <Badge data-testid="status-opened" className="bg-emerald-100 text-emerald-800">Ouvert</Badge>;
  if (status === "proxy_only")
    return <Badge data-testid="status-proxy" className="bg-amber-100 text-amber-800">Proxy Gmail</Badge>;
  return <Badge data-testid="status-never" variant="outline">Jamais ouvert</Badge>;
}

function formatLocal(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function CeoMailbox() {
  const { user } = useAuth();
  const isAllowed = (user?.email || "").toLowerCase() === "sami.khanfir@ba-inmotion.com";

  // ── Composer state ────────────────────────────────────────────────
  const [aliases, setAliases] = useState([]);
  const [aliasesWarning, setAliasesWarning] = useState("");
  const [fromMode, setFromMode] = useState(""); // alias email OR FREE_FROM_VALUE
  const [fromFree, setFromFree] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("Bonjour,\n\n");
  // Iter-285 — markdown-lite formatting toolbar for the body textarea.
  const bodyRef = useRef(null);
  const applyFormat = (type) => {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? bodyText.length;
    const end = el?.selectionEnd ?? bodyText.length;
    const sel = bodyText.slice(start, end);
    let ins = "";
    let cursor = [start, end];
    const wrap = (pre, post, ph) => {
      const t = sel || ph;
      ins = pre + t + post;
      cursor = [start + pre.length, start + pre.length + t.length];
    };
    const prefixLines = (numbered) => {
      const t = sel || "élément";
      ins = t.split("\n").map((l, i) => (numbered ? `${i + 1}. ` : "- ") + l).join("\n");
      cursor = [start, start + ins.length];
    };
    if (type === "bold") wrap("**", "**", "texte en gras");
    else if (type === "italic") wrap("*", "*", "texte en italique");
    else if (type === "underline") wrap("__", "__", "texte souligné");
    else if (type === "heading") wrap("## ", "", "Titre");
    else if (type === "ul") prefixLines(false);
    else if (type === "ol") prefixLines(true);
    else if (type === "link") wrap("[", "](https://)", "libellé du lien");
    setBodyText(bodyText.slice(0, start) + ins + bodyText.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(cursor[0], cursor[1]); });
  };
  const [previewHtml, setPreviewHtml] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  // ── Signature state ───────────────────────────────────────────────
  const [signatureHtml, setSignatureHtml] = useState("");
  const [savedSignature, setSavedSignature] = useState("");
  const [logoPosition, setLogoPosition] = useState("inline-left");
  const [savedLogoPosition, setSavedLogoPosition] = useState("inline-left");
  const [savingSignature, setSavingSignature] = useState(false);
  // Iter-228f — rich WYSIWYG editor (mirrors the Invoice email signature
  // editor in MaintenanceSettings). The contentEditable ref + server-rendered
  // preview html let the user format the signature like a Gmail signature
  // and see the BAIM logo placed exactly as it will appear in real emails.
  const sigEditorRef = useRef(null);
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("");
  const isSignatureDirty = signatureHtml !== savedSignature || logoPosition !== savedLogoPosition;
  const exec = (cmd, value = null) => {
    // document.execCommand is deprecated but still the most reliable way to
    // implement a lightweight rich-text editor across browsers. Acceptable
    // here since we render the resulting HTML server-side and sanitize.
    document.execCommand(cmd, false, value);
    if (sigEditorRef.current) setSignatureHtml(sigEditorRef.current.innerHTML);
  };
  const refreshSignaturePreview = async () => {
    try {
      const r = await api.post("/admin/ceo-mailbox/signature/preview", {
        signature_html: signatureHtml,
        logo_position: logoPosition,
      });
      setSignaturePreviewHtml(r.data?.signature_html || "");
    } catch (e) {
      toast.error("Aperçu indisponible");
    }
  };
  const resetSignature = () => {
    setSignatureHtml("");
    if (sigEditorRef.current) sigEditorRef.current.innerHTML = "";
    setLogoPosition("inline-left");
  };

  // ── History state ─────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [openSnap, setOpenSnap] = useState(null); // { token, rendered_html, opens, … }

  // ── Boot ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAllowed) return;
    (async () => {
      try {
        const r = await api.get("/admin/ceo-mailbox/aliases");
        setAliases(r.data?.aliases || []);
        if (r.data?.warning) setAliasesWarning(r.data.warning);
        const primary = r.data?.primary_email || "";
        const defaultAlias = (r.data?.aliases || []).find((a) => a.is_default) || (r.data?.aliases || []).find((a) => a.is_primary);
        setFromMode(defaultAlias?.send_as_email || primary || FREE_FROM_VALUE);
      } catch (e) { /* ignore */ }
      try {
        const r = await api.get("/admin/ceo-mailbox/signature");
        setSignatureHtml(r.data?.signature_html || "");
        setSavedSignature(r.data?.signature_html || "");
        setLogoPosition(r.data?.logo_position || "inline-left");
        setSavedLogoPosition(r.data?.logo_position || "inline-left");
      } catch (e) { /* ignore */ }
      loadHistory();
    })();
  }, [isAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter-228f — seed the contentEditable editor with the saved signature
  // on mount + whenever the saved html changes. Also refresh the server-side
  // rendered preview so the user sees the BAIM logo placed correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAllowed) return;
    if (sigEditorRef.current && sigEditorRef.current.innerHTML !== savedSignature) {
      sigEditorRef.current.innerHTML = savedSignature;
      setSignatureHtml(savedSignature);
    }
    (async () => {
      try {
        const r = await api.post("/admin/ceo-mailbox/signature/preview", {
          signature_html: savedSignature,
          logo_position: savedLogoPosition,
        });
        setSignaturePreviewHtml(r.data?.signature_html || "");
      } catch (e) { /* ignore */ }
    })();
  }, [savedSignature, savedLogoPosition, isAllowed]);

  // ── Live preview ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isAllowed) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/admin/ceo-mailbox/preview", { subject, body_text: bodyText });
        setPreviewHtml(r.data?.html || "");
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [bodyText, subject, savedSignature, savedLogoPosition, isAllowed]);

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const r = await api.get("/admin/ceo-mailbox/history");
      setHistory(r.data?.items || []);
    } catch (e) {
      toast.error("Impossible de charger l'historique");
    } finally {
      setHistoryLoading(false);
    }
  };

  const totalAttachBytes = useMemo(() => files.reduce((s, f) => s + (f.size || 0), 0), [files]);
  const totalAttachOverLimit = totalAttachBytes > MAX_TOTAL_MB * 1024 * 1024;
  const composerValid = (to || "").trim() && (subject || "").trim() && !totalAttachOverLimit && !sending;

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const saveSignature = async () => {
    setSavingSignature(true);
    try {
      const r = await api.put("/admin/ceo-mailbox/signature", {
        signature_html: signatureHtml,
        logo_position: logoPosition,
      });
      setSavedSignature(r.data?.signature_html || "");
      setSavedLogoPosition(r.data?.logo_position || "inline-left");
      toast.success("Signature enregistrée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingSignature(false);
    }
  };

  const sendEmail = async () => {
    if (!composerValid) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", to);
      if (cc) fd.append("cc", cc);
      if (bcc) fd.append("bcc", bcc);
      fd.append("subject", subject);
      fd.append("body_text", bodyText);
      const fromValue = fromMode === FREE_FROM_VALUE ? fromFree : fromMode;
      if (fromValue) fd.append("from_alias", fromValue);
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/admin/ceo-mailbox/send", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Email envoyé · token ${r.data?.tracking_token?.slice(0, 8)}…`);
      // Reset composer
      setTo(""); setCc(""); setBcc(""); setSubject("");
      setBodyText("Bonjour,\n\n");
      setFiles([]);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const openHistoryRow = async (row) => {
    try {
      const r = await api.get(`/admin/ceo-mailbox/history/${row.token}/preview`);
      setOpenSnap({ ...row, ...r.data });
    } catch (e) {
      toast.error("Snapshot introuvable");
    }
  };

  const archiveRow = async (token) => {
    if (!window.confirm("Masquer cet envoi de l'historique ? (le tracking est conservé)")) return;
    try {
      await api.delete(`/admin/ceo-mailbox/history/${token}`);
      toast.success("Email retiré de l'historique");
      loadHistory();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  if (!isAllowed) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card data-testid="ceo-mailbox-forbidden">
          <CardHeader>
            <CardTitle>CEO Mailbox</CardTitle>
            <CardDescription>Cette page est réservée à un compte spécifique.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="ceo-mailbox-page">
      <div className="flex items-center gap-3">
        <ScrollText className="size-6 text-brand-navy" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">CEO Mailbox</h1>
          <p className="text-sm text-muted-foreground">
            Composez et envoyez un email depuis un alias Gmail, avec pixel de suivi automatique.
          </p>
        </div>
      </div>

      <Tabs defaultValue="compose" className="w-full">
        <TabsList data-testid="ceo-mailbox-tabs">
          <TabsTrigger value="compose" data-testid="tab-compose">Composer</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historique</TabsTrigger>
          <TabsTrigger value="signature" data-testid="tab-signature">Signature</TabsTrigger>
        </TabsList>

        {/* ───────── COMPOSE ───────── */}
        <TabsContent value="compose" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="composer-card">
              <CardHeader>
                <CardTitle className="text-lg">Composer</CardTitle>
                <CardDescription>
                  Le pixel de tracking 1×1 et votre signature sont injectés automatiquement avant l&apos;envoi.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* From */}
                <div className="space-y-1.5">
                  <Label htmlFor="from-mode">From (alias Gmail)</Label>
                  <Select value={fromMode} onValueChange={setFromMode}>
                    <SelectTrigger id="from-mode" data-testid="from-select">
                      <SelectValue placeholder="Choisir un alias..." />
                    </SelectTrigger>
                    <SelectContent>
                      {aliases.map((a) => (
                        <SelectItem key={a.send_as_email} value={a.send_as_email} data-testid={`from-opt-${a.send_as_email}`}>
                          {a.display_name ? `${a.display_name} <${a.send_as_email}>` : a.send_as_email}
                          {a.is_primary ? " · primaire" : ""}{a.is_default ? " · par défaut" : ""}
                        </SelectItem>
                      ))}
                      <SelectItem value={FREE_FROM_VALUE} data-testid="from-opt-free">Autre (saisie libre)…</SelectItem>
                    </SelectContent>
                  </Select>
                  {fromMode === FREE_FROM_VALUE && (
                    <Input
                      data-testid="from-free-input"
                      placeholder='ex. "Sami Khanfir" <facturation@ba-inmotion.com>'
                      value={fromFree}
                      onChange={(e) => setFromFree(e.target.value)}
                    />
                  )}
                  {aliasesWarning && aliases.length === 0 && (
                    <p className="text-xs text-amber-700">
                      Gmail n&apos;a pas pu lister vos alias (scope manquant). Utilisez la saisie libre.
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="to">To*</Label>
                  <Input id="to" data-testid="to-input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinataire@example.com, autre@…" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="cc">Cc</Label>
                    <Input id="cc" data-testid="cc-input" value={cc} onChange={(e) => setCc(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bcc">Bcc</Label>
                    <Input id="bcc" data-testid="bcc-input" value={bcc} onChange={(e) => setBcc(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="subject">Subject*</Label>
                  <Input id="subject" data-testid="subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="body">Corps du message</Label>
                  <div className="flex items-center gap-1 flex-wrap border border-zinc-200 rounded-md bg-zinc-50/60 px-1.5 py-1" data-testid="body-format-toolbar">
                    {[
                      { t: "bold", Icon: Bold, tip: "Gras (**texte**)" },
                      { t: "italic", Icon: Italic, tip: "Italique (*texte*)" },
                      { t: "underline", Icon: Underline, tip: "Souligné (__texte__)" },
                      { t: "heading", Icon: Heading2, tip: "Titre (## Titre)" },
                      { t: "ul", Icon: List, tip: "Liste à puces (- élément)" },
                      { t: "ol", Icon: ListOrdered, tip: "Liste numérotée (1. élément)" },
                      { t: "link", Icon: Link2, tip: "Lien ([libellé](url))" },
                    ].map(({ t, Icon, tip }) => (
                      <button
                        key={t}
                        type="button"
                        title={tip}
                        onClick={() => applyFormat(t)}
                        data-testid={`body-format-${t}`}
                        className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-white hover:shadow-sm text-zinc-600 hover:text-zinc-900 border border-transparent hover:border-zinc-200"
                      >
                        <Icon size={14} />
                      </button>
                    ))}
                  </div>
                  <Textarea
                    id="body"
                    data-testid="body-input"
                    ref={bodyRef}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    className="text-sm leading-relaxed min-h-[220px]"
                    placeholder="Écrivez votre message normalement. Les retours à la ligne sont conservés."
                  />
                  <p className="text-[10px] text-muted-foreground italic">
                    Formatage : **gras**, *italique*, __souligné__, ## titre, listes (- ou 1.), [lien](url) — le rendu apparaît dans l&apos;aperçu. La signature configurée est ajoutée automatiquement.
                  </p>
                </div>

                {/* Attachments */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Pièces jointes ({bytesToHuman(totalAttachBytes)} / {MAX_TOTAL_MB} MB max)</Label>
                    <Button
                      variant="outline" size="sm"
                      data-testid="attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="size-4 mr-1" /> Ajouter
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={onPickFiles}
                      className="hidden"
                      data-testid="file-picker"
                    />
                  </div>
                  {totalAttachOverLimit && (
                    <p className="text-xs text-red-600">Limite dépassée — supprimez des fichiers.</p>
                  )}
                  {files.length > 0 && (
                    <div className="space-y-1">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-center justify-between text-xs border rounded px-2 py-1">
                          <span className="truncate" data-testid={`attach-${i}`}>
                            {f.name} <span className="text-muted-foreground">({bytesToHuman(f.size)})</span>
                          </span>
                          <Button variant="ghost" size="icon" data-testid={`attach-remove-${i}`} onClick={() => removeFile(i)}>
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  className="w-full"
                  data-testid="send-btn"
                  onClick={sendEmail}
                  disabled={!composerValid}
                >
                  <Send className="size-4 mr-1.5" />
                  {sending ? "Envoi en cours…" : "Envoyer"}
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card data-testid="preview-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="size-4" /> Aperçu (final + signature)
                </CardTitle>
                <CardDescription>
                  Le pixel n&apos;apparaît PAS ici (1×1 invisible). Il est inséré au moment de l&apos;envoi.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded bg-white p-4 min-h-[420px]" data-testid="preview-frame">
                  <div className="text-xs text-muted-foreground mb-2 border-b pb-2">
                    <div><strong>Sujet :</strong> {subject || <em>(aucun sujet)</em>}</div>
                    <div><strong>De :</strong> {fromMode === FREE_FROM_VALUE ? fromFree : fromMode || <em>(aucun)</em>}</div>
                    <div><strong>À :</strong> {to || <em>(aucun)</em>}</div>
                    {cc && <div><strong>Cc :</strong> {cc}</div>}
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ───────── HISTORY ───────── */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Historique des envois</CardTitle>
                <CardDescription>Dernières 100 entrées. Cliquez sur une ligne pour voir le rendu et les ouvertures.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadHistory} data-testid="history-refresh">
                <RefreshCw className="size-4 mr-1" /> Rafraîchir
              </Button>
            </CardHeader>
            <CardContent>
              <Table data-testid="history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>De</TableHead>
                    <TableHead>À</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead className="text-center">Opens</TableHead>
                    <TableHead>Dernière ouverture</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Chargement…</TableCell></TableRow>
                  )}
                  {!historyLoading && history.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Aucun email envoyé pour l&apos;instant.</TableCell></TableRow>
                  )}
                  {history.map((row) => (
                    <TableRow key={row.token} data-testid={`history-row-${row.token}`} className="hover:bg-muted/50 cursor-pointer" onClick={() => openHistoryRow(row)}>
                      <TableCell className="text-xs whitespace-nowrap">{formatLocal(row.sent_at)}</TableCell>
                      <TableCell className="text-xs">{row.from_alias || <span className="text-muted-foreground">(défaut)</span>}</TableCell>
                      <TableCell className="text-xs">{(row.to || []).join(", ")}</TableCell>
                      <TableCell className="max-w-[280px] truncate">{row.subject}</TableCell>
                      <TableCell className="text-center font-mono text-xs">{row.open_count || 0}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{formatLocal(row.last_open_at)}</TableCell>
                      <TableCell><StatusBadge status={row.status} /></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" data-testid={`history-archive-${row.token}`} onClick={() => archiveRow(row.token)}>
                          <Trash2 className="size-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ───────── SIGNATURE ───────── */}
        <TabsContent value="signature" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="ceo-signature-editor-card">
              <CardHeader>
                <div className="flex items-center gap-3 text-zinc-900">
                  <Mail size={18} className="text-zinc-700" />
                  <CardTitle className="text-lg">CEO Mailbox · Signature</CardTitle>
                </div>
                <CardDescription>
                  Compose the HTML signature appended at the end of every email sent from this page and pick where the BAIM logo should sit relative to the text. Leaving the editor empty falls back to the legacy BAIM signature block. <strong>This signature is independent of the Invoice email signature.</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Logo position picker */}
                <div className="space-y-2" data-testid="ceo-signature-logo-position-block">
                  <Label className="text-xs">Logo position</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "top", label: "Top" },
                      { v: "inline-left", label: "Inline (left)" },
                      { v: "bottom", label: "Bottom" },
                      { v: "none", label: "No logo" },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setLogoPosition(opt.v)}
                        data-testid={`ceo-signature-logo-pos-${opt.v}`}
                        className={`px-3 h-9 rounded-md text-xs font-medium transition-colors ${
                          logoPosition === opt.v
                            ? "bg-brand-navy text-white"
                            : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Toolbar + contentEditable HTML editor */}
                <div className="space-y-2">
                  <Label className="text-xs">Signature content</Label>
                  <div className="border border-zinc-200 rounded-md overflow-hidden">
                    <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-zinc-100 bg-zinc-50" data-testid="ceo-signature-toolbar">
                      <button type="button" onClick={() => exec("bold")} data-testid="ceo-sig-btn-bold" title="Bold" className="w-8 h-8 rounded hover:bg-zinc-200 font-bold text-sm">B</button>
                      <button type="button" onClick={() => exec("italic")} data-testid="ceo-sig-btn-italic" title="Italic" className="w-8 h-8 rounded hover:bg-zinc-200 italic text-sm">I</button>
                      <button type="button" onClick={() => exec("underline")} data-testid="ceo-sig-btn-underline" title="Underline" className="w-8 h-8 rounded hover:bg-zinc-200 underline text-sm">U</button>
                      <span className="w-px h-5 bg-zinc-300 mx-1" />
                      <label className="inline-flex items-center gap-1 text-[10px] uppercase font-mono text-zinc-500 cursor-pointer">
                        <span>Color</span>
                        <input type="color" defaultValue="#1e5db4" onChange={(e) => exec("foreColor", e.target.value)} data-testid="ceo-sig-btn-color" className="w-6 h-6 rounded border border-zinc-200 cursor-pointer" />
                      </label>
                      <span className="w-px h-5 bg-zinc-300 mx-1" />
                      <button type="button" onClick={() => { const url = window.prompt("Link URL", "https://"); if (url) exec("createLink", url); }} data-testid="ceo-sig-btn-link" title="Insert link" className="px-2 h-8 rounded hover:bg-zinc-200 text-xs">Link</button>
                      <button type="button" onClick={() => exec("removeFormat")} data-testid="ceo-sig-btn-clear" title="Clear formatting" className="px-2 h-8 rounded hover:bg-zinc-200 text-xs text-zinc-500">Clear</button>
                    </div>
                    <div
                      ref={sigEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      data-testid="ceo-sig-editor"
                      onInput={(e) => setSignatureHtml(e.currentTarget.innerHTML)}
                      onBlur={(e) => setSignatureHtml(e.currentTarget.innerHTML)}
                      className="min-h-[180px] p-3 text-sm focus:outline-none"
                      style={{ fontFamily: "Verdana, Geneva, sans-serif" }}
                    />
                  </div>
                  <div className="text-[10px] text-zinc-500 italic">
                    Tip: type your contact details (e.g. <em>Sami KHANFIR — CEO BAIM — sami@ba-inmotion.ma</em>) here. The BAIM logo is added automatically at the chosen position.
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
                  <button onClick={resetSignature} data-testid="ceo-sig-reset" className="text-[11px] font-mono text-zinc-500 hover:text-zinc-800 uppercase">Reset to default</button>
                  <button onClick={saveSignature} disabled={savingSignature || !isSignatureDirty} data-testid="ceo-sig-save" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
                    <Save size={14} /> {savingSignature ? "Saving…" : "Save signature"}
                  </button>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="ceo-signature-preview-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Preview</CardTitle>
                  <button
                    type="button"
                    onClick={refreshSignaturePreview}
                    data-testid="ceo-sig-preview-refresh"
                    className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider rounded-md hover:bg-zinc-50"
                  >
                    Apply &amp; preview
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div
                  className="border border-dashed border-zinc-300 rounded-md p-4 bg-zinc-50/50 min-h-[300px]"
                  data-testid="ceo-sig-preview"
                  dangerouslySetInnerHTML={{ __html: signaturePreviewHtml }}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* History detail modal */}
      <Dialog open={!!openSnap} onOpenChange={(o) => !o && setOpenSnap(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-detail-modal">
          <DialogHeader>
            <DialogTitle>{openSnap?.subject || "Email"}</DialogTitle>
          </DialogHeader>
          {openSnap && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><strong>Envoyé le :</strong> {formatLocal(openSnap.sent_at)}</div>
                <div><strong>From :</strong> {openSnap.from_alias || "(défaut)"}</div>
                <div className="col-span-2"><strong>To :</strong> {(openSnap.to || []).join(", ")}</div>
                {openSnap.cc?.length > 0 && <div className="col-span-2"><strong>Cc :</strong> {openSnap.cc.join(", ")}</div>}
                {openSnap.bcc?.length > 0 && <div className="col-span-2"><strong>Bcc :</strong> {openSnap.bcc.join(", ")}</div>}
                <div><strong>Opens :</strong> {openSnap.open_count || 0}</div>
                <div><strong>Statut :</strong> <StatusBadge status={openSnap.status} /></div>
              </div>
              {openSnap.attachments?.length > 0 && (
                <div className="text-xs">
                  <strong>Pièces jointes :</strong>
                  <ul className="list-disc list-inside mt-1">
                    {openSnap.attachments.map((a, i) => (
                      <li key={i}>{a.filename} <span className="text-muted-foreground">({bytesToHuman(a.size_bytes)})</span></li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="text-xs font-semibold mb-1">Rendu HTML envoyé</div>
                <div className="border rounded bg-white p-3 max-h-[300px] overflow-auto" data-testid="snap-html">
                  <div dangerouslySetInnerHTML={{ __html: openSnap.rendered_html || "" }} />
                </div>
              </div>
              {openSnap.opens?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1">Détail des ouvertures</div>
                  <div className="text-xs border rounded divide-y" data-testid="snap-opens">
                    {openSnap.opens.map((o, i) => (
                      <div key={i} className="px-2 py-1.5 grid grid-cols-3 gap-2">
                        <div>{formatLocal(o.at)}</div>
                        <div className="font-mono text-[10px] truncate">{o.ip || "—"}</div>
                        <div className="text-[10px] truncate">{o.user_agent || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSnap(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
